// GraphQL query builder utilities for Kiddom API
// Builds GraphQL query strings from structured query definitions

function isObject(paramObj: unknown): boolean {
  return (
    paramObj !== null &&
    !Array.isArray(paramObj) &&
    typeof paramObj === 'object'
  )
}

function isArrayOfObjects(paramObj: unknown): boolean {
  if (Array.isArray(paramObj) && paramObj.length > 0) {
    if (Array.isArray(paramObj[0])) {
      return isArrayOfObjects(paramObj[0])
    }
    return isObject(paramObj[0])
  }
  return false
}

export type IQueryParam = string | number | object | boolean

export type fieldList = Array<string | ObjectField>

interface ObjectField {
  [key: string]: fieldList
}

export interface ResourceQuery {
  key: string
  fields?: fieldList
}

/**
 * Builds a GraphQL query string from multiple query definitions
 * @param queries - Array of query definitions with key, params, fields, and optional alias
 * @returns GraphQL query string
 */
export function buildQueries(
  queries: {
    key: string
    params?: { [key: string]: IQueryParam }
    fields?: fieldList
    alias?: string
  }[],
): string {
  const queryStrings = queries.map(({ key, params = {}, fields, alias }) => {
    const paramList = Object.entries(params)
      .map(([key, paramValue]) => {
        if (isArrayOfObjects(paramValue) || isObject(paramValue)) {
          return `${key}:${buildObjectParamString(paramValue)}`
        } else {
          return `${key}: ${JSON.stringify(paramValue)}`
        }
      })
      .join(',')
    const fieldList = fields && reduceFields(fields)
    const paramsPart = Object.keys(paramList).length ? `(${paramList})` : ''
    const fieldsParam = fieldList ? `{${fieldList}}` : ''
    const aliasParam = alias ? `${alias}: ` : ''
    return `${aliasParam}${key}${paramsPart}${fieldsParam}`
  })
  return `{${queryStrings.join(' ')}}`
}

/**
 * Recursively reduces field definitions into a GraphQL field selection string
 * @param fields - Array of field names or nested field objects
 * @returns GraphQL field selection string
 */
function reduceFields(fields: fieldList): string {
  return fields
    .reduce((acc: string, field) => {
      if (typeof field === 'string') {
        acc = `${acc} ${field}`
      } else {
        const fieldStrings = Object.keys(field).map(
          (k) => `${k} {${reduceFields(field[k])}}`,
        )
        acc = `${acc} ${fieldStrings.join(' ')}`
      }
      return acc
    }, '')
    .trim()
}

/**
 * Converts JavaScript objects/arrays into GraphQL parameter format
 * @param paramObj - Object or array to convert
 * @returns GraphQL-formatted parameter string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildObjectParamString(paramObj: any): string {
  let jsonStr = ''
  if (Array.isArray(paramObj)) {
    jsonStr = '['
    for (let i = 0; i < paramObj.length; i++) {
      jsonStr += buildObjectParamString(paramObj[i])
      if (i + 1 < paramObj.length) {
        jsonStr += ','
      }
    }
    jsonStr += ']'
  } else {
    jsonStr =
      '{' +
      Object.entries(paramObj)
        .map(([key, paramValue]) => {
          if (isObject(paramValue) || isArrayOfObjects(paramValue)) {
            return `${key}:` + buildObjectParamString(paramValue)
          } else {
            return `${key}: ${JSON.stringify(paramValue)}`
          }
        })
        .join(',') +
      '}'
  }
  return jsonStr
}
